import { useState, useRef, useEffect, useCallback } from "react";
import {
  ArrowDown,
  X,
  Search,
  Star,
  MoreHorizontal,
  AlertTriangle,
} from "lucide-react";
import { useSubscriptionGate } from "../hooks/useSubscriptionGate";
import { motion } from "framer-motion";
import { ChatFooter } from "../components/ChatFooter/ChatFooter";
import { useAttachments } from "../hooks/useAttachments";
import { useTokenCount } from "../hooks/useTokenCount";
import { useChat } from "../hooks/useChat";
import { useChatHistory } from "../hooks/useChatHistory";
import { useNotification } from "../hooks/useNotification";
import { useStreamingChat } from "../hooks/useStreamingChat";
import { ChatService } from "../services/api/chat";
import { ChatHeader, ChatMessage, HeroActions } from "../components/Chat";
import { NotificationContainer } from "../components/Notification";
import FeedbackModal from "../components/FeedbackModal";
import { useAuth } from "../contexts/AuthContext";
import GenerateInsight from "../assets/svg/generateInsight.svg";
import ConnectTools from "../assets/svg/connectTools.svg";
import SyncLatestData from "../assets/svg/syncLatestData.svg";
import CreateAutomation from "../assets/svg/createAutomation.svg";
import LogoSvg from "../assets/svg/logo.svg";
import LogoDark from "../assets/svg/logo_dark.svg";
import { isPWA } from "../lib/platform";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  attachments?: string[];
  error?: boolean;
}

const MAX_TOKENS = 8000;

// Feature flag: set to true to enable the feedback modal after N prompts
const ENABLE_FEEDBACK_MODAL = false;

const AssistantBadge = () => (
  <div className="h-8 w-8 overflow-hidden rounded-full">
    <img
      src={LogoSvg}
      alt="Assistant"
      className="h-full w-full object-cover dark:hidden"
    />
    <img
      src={LogoDark}
      alt="Assistant"
      className="h-full w-full object-cover hidden dark:block"
    />
  </div>
);

/**
 * - Markdown rendering with accordion support
 * - File attachments
 * - Feedback (like/dislike)
 * - Copy functionality
 * - Retry mechanism
 * - iOS safe-area support
 */
export function Chat() {
  const [messages, setMessages] = useState<Message[]>([]);
  // LocalStorage key for chat ID persistence
  const CHAT_ID_KEY = "currentChatId";

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [showScrollDownButton, setShowScrollDownButton] = useState(false);

  const [feedbackStatus, setFeedbackStatus] = useState<
    Map<string, "like" | "dislike">
  >(new Map());
  const [copyStatus, setCopyStatus] = useState<Map<string, boolean>>(new Map());

  // Feedback modal state - show after 2-3 user prompts
  const [userPromptCount, setUserPromptCount] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const feedbackShownRef = useRef(false); // Use ref to avoid stale closure issues

  // Token quota limit state
  const [showTokenLimitModal, setShowTokenLimitModal] = useState(false);
  const { isFree: isFreeFromGate } = useSubscriptionGate();

  // Debug: Log when showFeedbackModal changes
  useEffect(() => {
    console.log("[Feedback] showFeedbackModal changed:", showFeedbackModal);
  }, [showFeedbackModal]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  const [composerHasFocus, setComposerHasFocus] = useState(false);

  // Get user data from AuthContext
  const { user } = useAuth();
  const userName = user?.name || "User";

  const [sideBar, setSideBar] = useState(false);

  // Use real API hooks
  const {
    chats: sideBarChats,
    loading: loadingChats,
    createChat,
    deleteChat: apiDeleteChat,
    renameChat: apiRenameChat,
    refreshChats,
  } = useChat();
  const { messages: apiMessages, loadHistory, clearHistory } = useChatHistory();

  // Notification system
  const { notifications, removeNotification, success, error } =
    useNotification();

  // Streaming chat hook for chain-of-thought UI
  const {
    isStreaming,
    thinkingMessage,
    startStream,
    reset: resetStream,
  } = useStreamingChat({
    onThinkingChange: (message) => {
      // Update thinking state like extension does
      console.log("Thinking status:", message);
    },
    onAnswerChunk: (_chunk, fullText, messageId) => {
      // Update messages array DIRECTLY during streaming (like extension's setHistory)
      // This ensures content shows immediately as it streams
      setThinking(false); // Hide thinking indicator when answer starts

      setMessages((prev) => {
        const newMessages = [...prev];
        // Find the last assistant message that's streaming
        const lastMsg = newMessages[newMessages.length - 1];
        const isLastMsgStreamingAssistant =
          lastMsg && lastMsg.role === "assistant" && !lastMsg.error;

        if (isLastMsgStreamingAssistant) {
          // Update existing streaming message
          newMessages[newMessages.length - 1] = {
            ...lastMsg,
            content: fullText,
            id: messageId || lastMsg.id,
          };
        } else {
          // Add new assistant message for streaming
          newMessages.push({
            id:
              messageId ||
              `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            role: "assistant",
            content: fullText,
            timestamp: new Date(),
          });
        }
        return newMessages;
      });
    },
    onStreamEnd: (msgId) => {
      console.log("[Feedback] onStreamEnd called with msgId:", msgId);
      // Update the final message ID when stream completes
      if (msgId) {
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastIndex = newMessages.length - 1;
          if (lastIndex >= 0 && newMessages[lastIndex].role === "assistant") {
            newMessages[lastIndex] = {
              ...newMessages[lastIndex],
              id: msgId,
            };
          }
          return newMessages;
        });
      }
      setIsLoading(false);
      setThinking(false);
      // Defer resetStream to allow React to process the message state update first
      setTimeout(() => resetStream(), 100);

      // Increment prompt count and show feedback modal after 2-3 prompts
      setUserPromptCount((prev) => {
        const newCount = prev + 1;
        console.log(
          `[Feedback] Prompt count: ${newCount}, feedbackShown: ${feedbackShownRef.current}`,
        );
        // Show feedback after 3 prompts, only once per session
        if (
          ENABLE_FEEDBACK_MODAL &&
          newCount === 3 &&
          !feedbackShownRef.current
        ) {
          console.log("[Feedback] Showing feedback modal in 1.5s");
          setTimeout(() => {
            console.log("[Feedback] Setting showFeedbackModal to true");
            setShowFeedbackModal(true);
          }, 1500); // Delay 1.5s after response completes
          feedbackShownRef.current = true;
        }
        return newCount;
      });
    },
    onError: (err, errorType) => {
      if (errorType === "TokenLimitExceeded") {
        setShowTokenLimitModal(true);
        setIsLoading(false);
        setThinking(false);
        return;
      }
      const errorMessage: Message = {
        id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: "assistant",
        content: `Sorry, I encountered an error: ${err || "Please try again."}`,
        timestamp: new Date(),
        error: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
      setThinking(false);
    },
  });

  // Track current active chat ID
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);

  const [activeHistoryTab, setActiveHistoryTab] = useState(0);
  const [allHistorySearch, setAllHistorySearch] = useState("");
  const [favoritesHistorySearch, setFavoritesHistorySearch] = useState("");
  const [favoriteChatIds, setFavoriteChatIds] = useState<Set<string>>(
    new Set(),
  );
  const [historyMenuState, setHistoryMenuState] = useState<{
    chatId: string | null;
    placement: "above" | "below";
  }>({ chatId: null, placement: "below" });
  const historyScrollRef = useRef<HTMLDivElement | null>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [selectedChatForRename, setSelectedChatForRename] = useState<{
    chat_id: string;
    title?: string;
  } | null>(null);
  const [renameInputValue, setRenameInputValue] = useState("");

  // Hero Actions configuration
  const heroActions = [
    {
      id: 1,
      label: "Ask about an issue",
      Icon: GenerateInsight,
    },
    {
      id: 2,
      label: "What can you do?",
      Icon: ConnectTools,
    },
    {
      id: 3,
      label: "Find root causes",
      Icon: SyncLatestData,
    },
    {
      id: 4,
      label: "Summarize these logs",
      Icon: CreateAutomation,
    },
  ];

  const heroActionsDisabled = false; // Set to true to disable hero actions

  const handleHeroAction = useCallback(
    async (text: string) => {
      if (heroActionsDisabled || isLoading) return;

      const userMessage: Message = {
        id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: "user",
        content: text,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setIsLoading(true);
      setThinking(true);

      try {
        let chatId = currentChatId;

        // Check if we need to create a new chat
        if (!chatId) {
          console.log("Creating new chat for hero action...");

          // Create chat session (no title parameter needed)
          const startChatResponse = await createChat();
          chatId = startChatResponse.chat_id;
          localStorage.setItem(CHAT_ID_KEY, chatId);
          setCurrentChatId(chatId);
          await refreshChats();
        }

        // Use streaming API for real-time chain-of-thought UI
        await startStream({
          chat_id: chatId,
          message: text,
        });
      } catch (error) {
        console.error("Failed to send hero action:", error);

        const errorMessage: Message = {
          id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          role: "assistant",
          content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : "Please try again."}`,
          timestamp: new Date(),
          error: true,
        };

        setMessages((prev) => [...prev, errorMessage]);
        setIsLoading(false);
        setThinking(false);
      }
    },
    [
      heroActionsDisabled,
      isLoading,
      currentChatId,
      createChat,
      refreshChats,
      startStream,
    ],
  );

  const {
    attachments,
    readyAttachments,
    isDragActive,
    liveRegionRef,
    removeAttachment,
    clearAttachments,
    handleFileInputChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useAttachments();

  const tokenInfo = useTokenCount({
    composerText: input,
    attachments,
    maxTokens: MAX_TOKENS,
  });

  const hasPendingAttachments = attachments.some(
    (attachment) => attachment.status === "pending",
  );

  const canSendMessage =
    !isLoading &&
    input.trim().length > 0 &&
    !hasPendingAttachments &&
    !tokenInfo.isOverLimit;

  // Merge local messages with API messages
  // If we have loaded history (apiMessages), use that + any new local messages
  // Otherwise use local messages only
  const displayMessages =
    apiMessages.length > 0
      ? [
          ...apiMessages.map((msg, index) => ({
            id: msg.id || `api-msg-${currentChatId}-${index}`,
            role: msg.role as "user" | "assistant",
            content: msg.content,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          })),
          ...messages,
        ]
      : messages;

  // Preserve scroll position when footer height changes
  useEffect(() => {
    if (!footerRef.current || !chatBodyRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Keep scroll at bottom if user was already at bottom
      if (!chatBodyRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < 50) {
        chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
      }
    });

    resizeObserver.observe(footerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // ===== iOS safe-area & keyboard stability =====
  useEffect(() => {
    const updateVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--app-vh", `${vh}px`);

      // Update body height for mobile
      document.body.style.height = `${window.innerHeight}px`;
    };
    updateVh();
    window.addEventListener("resize", updateVh);
    window.addEventListener("orientationchange", updateVh);
    return () => {
      window.removeEventListener("resize", updateVh);
      window.removeEventListener("orientationchange", updateVh);
    };
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    if (messagesEndRef.current && chatBodyRef.current) {
      // Check if user is near bottom before auto-scrolling
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Only auto-scroll if user is already near the bottom (within 200px)
      if (distanceFromBottom < 200 || behavior === "auto") {
        messagesEndRef.current.scrollIntoView({ behavior, block: "end" });
      }
    }
  }, []);

  // Auto-scroll only when new messages are added
  useEffect(() => {
    if (messages.length > 0) {
      // Use setTimeout to ensure DOM has updated
      const timer = setTimeout(() => scrollToBottom("smooth"), 100);
      return () => clearTimeout(timer);
    }
  }, [messages.length, scrollToBottom]);

  const handleChatScroll = useCallback(() => {
    if (!chatBodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    // Show button if user scrolled up more than 150px from bottom
    const shouldShow = distanceFromBottom > 150;
    setShowScrollDownButton(shouldShow);
  }, []);

  // Handle feedback submission
  const handleFeedbackSubmit = useCallback(
    (feedback: { rating: string; comment: string }) => {
      console.log("Feedback submitted:", feedback);
      // TODO: Send feedback to backend API
      success("Thank you for your feedback!");
    },
    [success],
  );

  const scrollToBottomSmooth = useCallback(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: "smooth",
      });
      // Hide button immediately
      setShowScrollDownButton(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (
      !input.trim() ||
      isLoading ||
      hasPendingAttachments ||
      tokenInfo.isOverLimit
    )
      return;

    const userMessageContent = input.trim();
    // Extract File objects from AttachmentItems
    const files = readyAttachments.map((attachment) => attachment.file);

    const userMessage: Message = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: "user",
      content: userMessageContent,
      timestamp: new Date(),
      attachments:
        readyAttachments.length > 0
          ? readyAttachments.map((a) => a.name)
          : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    clearAttachments();
    setIsLoading(true);
    setThinking(true);

    try {
      let chatId = currentChatId;

      // STEP 1: Check if we have a chat ID (new chat vs existing chat)
      if (!chatId) {
        const startChatResponse = await createChat();

        chatId = startChatResponse.chat_id;

        // Store chat ID in localStorage for persistence
        localStorage.setItem(CHAT_ID_KEY, chatId);
        setCurrentChatId(chatId);

        // Refresh sidebar to show new chat
        await refreshChats();
      }

      await startStream({
        chat_id: chatId,
        message: userMessageContent,
        files: files.length > 0 ? files : undefined,
      });
    } catch (error) {
      // Add error message to chat
      const errorMessage: Message = {
        id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: "assistant",
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : "Please try again."}`,
        timestamp: new Date(),
        error: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
      setThinking(false);
    }
  }, [
    input,
    isLoading,
    hasPendingAttachments,
    tokenInfo.isOverLimit,
    readyAttachments,
    clearAttachments,
    currentChatId,
    createChat,
    refreshChats,
    startStream,
  ]);

  const handleComposerKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleComposerPaste = () => {
    // Handle paste events if needed
  };

  const handleCopy = useCallback((text: string, messageId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyStatus((prev) => new Map(prev).set(messageId, true));
      setTimeout(() => {
        setCopyStatus((prev) => {
          const newMap = new Map(prev);
          newMap.delete(messageId);
          return newMap;
        });
      }, 2000);
    });
  }, []);

  const handleFeedback = useCallback(
    async (messageId: string, type: "like" | "dislike") => {
      // Update local UI state
      setFeedbackStatus((prev) => {
        const newMap = new Map(prev);
        const current = newMap.get(messageId);
        if (current === type) {
          newMap.delete(messageId);
        } else {
          newMap.set(messageId, type);
        }
        return newMap;
      });

      // Call API to persist feedback
      if (currentChatId) {
        try {
          await ChatService.reviewMessage(currentChatId, messageId, type);
          console.log(`Message ${messageId} reviewed as ${type}`);
        } catch (error) {
          console.error("Failed to submit review:", error);
          // Could show toast notification here
        }
      }
    },
    [currentChatId],
  );

  const handleRetry = useCallback(
    (messageId: string) => {
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      if (messageIndex === -1) return;

      // Find the user message before this assistant message
      const userMessage = messages
        .slice(0, messageIndex)
        .reverse()
        .find((m) => m.role === "user");

      if (userMessage) {
        setInput(userMessage.content);
        composerRef.current?.focus();
      }
    },
    [messages],
  );

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setInput("");
    clearAttachments();
    clearHistory();
    setCurrentChatId(null); // Reset chat ID for new chat
    // Clear localStorage when starting new chat
    localStorage.removeItem(CHAT_ID_KEY);
    // Reset feedback tracking for new chat
    setUserPromptCount(0);
    feedbackShownRef.current = false;
  }, [clearAttachments, clearHistory]);

  const handleHistoryClick = useCallback(() => {
    setSideBar(true);
  }, []);

  const toggleFavoriteChat = (chatId: string) => {
    setFavoriteChatIds((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      return next;
    });
  };

  const handleHistoryChatClick = async (chatId: string) => {
    try {
      // Load chat history from API
      await loadHistory(chatId);

      // Set as current chat
      setCurrentChatId(chatId);
      // Store in localStorage
      localStorage.setItem(CHAT_ID_KEY, chatId);

      // Clear local messages and use API messages instead
      setMessages([]);

      // Reset feedback tracking when switching chats
      setUserPromptCount(0);
      feedbackShownRef.current = false;

      // Close sidebar
      setSideBar(false);
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  const handleHistoryMenuToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, targetChatId: string) => {
      event.stopPropagation();
      const buttonEl = event.currentTarget;
      const buttonRect = buttonEl?.getBoundingClientRect?.();
      const viewportHeight =
        typeof window !== "undefined"
          ? window.innerHeight || document.documentElement?.clientHeight || 0
          : 0;
      const menuHeight = 180;
      const shouldOpenUpwards = buttonRect
        ? viewportHeight - buttonRect.bottom < menuHeight
        : false;

      setHistoryMenuState((prev) => {
        if (prev.chatId === targetChatId) {
          return { chatId: null, placement: "below" };
        }
        return {
          chatId: targetChatId,
          placement: shouldOpenUpwards ? "above" : "below",
        };
      });
    },
    [],
  );

  const handleHistoryActions = async (
    action: "rename" | "remove",
    chat: { chat_id: string; title?: string },
    newTitle?: string,
  ) => {
    try {
      switch (action) {
        case "rename":
          if (newTitle !== undefined && chat.chat_id) {
            const response = await apiRenameChat(chat.chat_id, newTitle);
            if (response.success) {
              success("Chat renamed successfully");
            }
          }
          break;
        case "remove":
          if (chat.chat_id) {
            const response = await apiDeleteChat(chat.chat_id);
            if (response.success) {
              success("Chat deleted successfully");
              // If this was the current chat, clear it
              if (chat.chat_id === currentChatId) {
                setCurrentChatId(null);
                localStorage.removeItem(CHAT_ID_KEY);
                setMessages([]);
                clearHistory();
              }
            }
          }
          break;
      }
      setHistoryMenuState({ chatId: null, placement: "below" });
    } catch (err) {
      console.error(`Failed to ${action} chat:`, err);
      error(
        err instanceof Error
          ? err.message
          : `Failed to ${action} chat. Please try again.`,
      );
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleClick = () =>
      setHistoryMenuState({ chatId: null, placement: "below" });
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Clear stale chat ID on fresh mount - user should start new chat or pick from history
  // This ensures start_chat is called when user sends their first message
  useEffect(() => {
    // Clear any stale chat ID from previous sessions
    // User will either:
    // 1. Send a message -> triggers start_chat -> creates new chat
    // 2. Click on a chat from history -> loads that chat
    localStorage.removeItem(CHAT_ID_KEY);
    setCurrentChatId(null);
    console.log("Fresh chat session - cleared stale chat ID");
  }, []);

  // Expose handlers to parent via window for Header to use
  useEffect(() => {
    if (typeof window !== "undefined") {
      const win = window as Window & {
        chatHandlers?: {
          handleNewChat: () => void;
          handleHistoryClick: () => void;
        };
      };
      win.chatHandlers = {
        handleNewChat,
        handleHistoryClick,
      };
    }
    return () => {
      if (typeof window !== "undefined") {
        const win = window as Window & { chatHandlers?: unknown };
        delete win.chatHandlers;
      }
    };
  }, [handleNewChat, handleHistoryClick]);

  const isInPWA = isPWA();

  return (
    <div className="flex bg-[#FAF6F1] dark:bg-[#1A2D47] overflow-hidden h-full">
      {/* Main Chat Area - render first so it's on the left */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Desktop Header - Only show when not in PWA */}
        {!isInPWA && (
          <ChatHeader
            onNewChat={handleNewChat}
            onHistoryClick={handleHistoryClick}
          />
        )}

        {/* Messages Area */}
        <div
          ref={chatBodyRef}
          onScroll={handleChatScroll}
          className={`flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar relative ${
            isInPWA ? "pt-14" : ""
          }`}
          style={{
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "1rem",
          }}
        >
          {displayMessages.length === 0 ? (
            <HeroActions
              userName={userName}
              actions={heroActions}
              onActionClick={handleHeroAction}
              disabled={heroActionsDisabled}
            />
          ) : (
            // Messages
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-8">
              {displayMessages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <ChatMessage
                    message={message}
                    feedbackStatus={feedbackStatus}
                    copyStatus={copyStatus}
                    onFeedback={handleFeedback}
                    onCopy={(msgId, content) => handleCopy(content, msgId)}
                    onRetry={handleRetry}
                  />
                </motion.div>
              ))}

              {/* Streaming response section - show while streaming OR thinking */}
              {(isStreaming || thinking) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col gap-2 pl-1"
                >
                  <AssistantBadge />
                  {/* Chain-of-thought thinking indicator - matches extension pattern */}
                  {thinking && (
                    <div className="flex items-center gap-2 bg-transparent py-2 text-sm text-[#8E8378] dark:text-[#9AAABF] w-fit">
                      <span>
                        {thinkingMessage || "Thinking"}
                        <span className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                      </span>
                    </div>
                  )}
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}

          {showScrollDownButton && (
            <div className="sticky bottom-4 left-0 right-0 flex justify-center pointer-events-none z-40">
              <button
                onClick={scrollToBottomSmooth}
                className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#E5DDD3] dark:border-[#455D7D] bg-white dark:bg-[#2C405F] text-[#2C405F] dark:text-[#9AAABF] shadow-xl transition-all hover:bg-[#F5EFE6] dark:hover:bg-[#455D7D] hover:scale-110 hover:shadow-2xl active:scale-95"
                aria-label="Scroll to bottom"
              >
                <ArrowDown size={20} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>

        {/* ChatGPT-style Input Component */}
        <footer
          ref={footerRef}
          className="flex-shrink-0 w-full px-4 py-3 bg-[#FAF6F1] dark:bg-[#1A2D47] flex justify-center"
          style={{
            paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
          }}
        >
          <div className="w-full max-w-3xl space-y-2">
            {/* Feedback Modal - appears above input when active */}
            {ENABLE_FEEDBACK_MODAL && showFeedbackModal && (
              <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                onSubmit={handleFeedbackSubmit}
              />
            )}

            <ChatFooter
              value={input}
              textareaRef={composerRef}
              onValueChange={setInput}
              onKeyDown={handleComposerKeyDown}
              onFocusChange={setComposerHasFocus}
              onPaste={handleComposerPaste}
              onSend={handleSubmit}
              canSend={canSendMessage}
              attachments={attachments}
              onRemoveAttachment={removeAttachment}
              onFileInputChange={handleFileInputChange}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              isDragActive={isDragActive}
              tokenInfo={tokenInfo}
              composerHasFocus={composerHasFocus}
            />

            <div
              ref={liveRegionRef}
              className="sr-only"
              aria-live="polite"
              aria-atomic="true"
            />
          </div>
        </footer>
      </div>
      {/* End Main Chat Area */}

      {/* Desktop Right Sidebar - History (only for non-PWA) */}
      {!isInPWA && sideBar && (
        <aside className="w-80 border-l border-[#E5DDD3] dark:border-[#2C405F] bg-[#FDFAF7] dark:bg-[#122135] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-[#E5DDD3] px-6 py-3 h-14">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold text-[#1D1B19] dark:text-[#E3E6EA]">
                Chat History
                <span className="text-sm font-normal text-[#8E8378] dark:text-[#9AAABF]">
                  ({sideBarChats.length})
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSideBar(false)}
              className="rounded-full bg-transparent p-2 text-[#8E8378] dark:text-[#9AAABF] transition hover:bg-[#F5EFE6] dark:hover:bg-[#2C405F]"
              aria-label="Close history"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="flex border-b border-[#E5DDD3] dark:border-[#2C405F]">
              <button
                type="button"
                onClick={() => setActiveHistoryTab(0)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  activeHistoryTab === 0
                    ? "border-b-2 border-[#2C405F] dark:border-[#6E829E] text-[#2C405F] dark:text-[#6E829E]"
                    : "text-[#8E8378] dark:text-[#9AAABF] hover:text-[#2C405F] dark:hover:text-[#6E829E]"
                }`}
              >
                All ({sideBarChats.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveHistoryTab(1)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  activeHistoryTab === 1
                    ? "border-b-2 border-[#2C405F] dark:border-[#6E829E] text-[#2C405F] dark:text-[#6E829E]"
                    : "text-[#8E8378] dark:text-[#9AAABF] hover:text-[#2C405F] dark:hover:text-[#6E829E]"
                }`}
              >
                Favorites ({favoriteChatIds.size})
              </button>
            </div>

            <div className="flex h-[calc(100%-60px)] flex-col gap-3 p-4">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8378] dark:text-[#9AAABF]"
                  size={16}
                />
                <input
                  type="text"
                  value={
                    activeHistoryTab === 0
                      ? allHistorySearch
                      : favoritesHistorySearch
                  }
                  onChange={(e) =>
                    activeHistoryTab === 0
                      ? setAllHistorySearch(e.target.value)
                      : setFavoritesHistorySearch(e.target.value)
                  }
                  placeholder="Search incident..."
                  className="h-10 w-full rounded-xl border border-[#E5DDD3] dark:border-[#455D7D] bg-white dark:bg-[#2C405F] pl-10 pr-4 text-sm text-[#1D1B19] dark:text-[#E3E6EA] placeholder-[#8E8378] dark:placeholder-[#9AAABF] focus:border-[#2C405F] dark:focus:border-[#6E829E] focus:outline-none focus:ring-2 focus:ring-[#2C405F]/20 dark:focus:ring-[#6E829E]/20"
                />
              </div>

              <div
                ref={historyScrollRef}
                className="flex-1 overflow-y-auto custom-scrollbar overflow-x-visible"
              >
                {loadingChats ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-sm text-[#8E8378]">
                      Loading chats...
                    </div>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {(() => {
                      const normalizedSearch = (
                        activeHistoryTab === 0
                          ? allHistorySearch
                          : favoritesHistorySearch
                      )
                        .trim()
                        .toLowerCase();
                      const chatsToShow =
                        activeHistoryTab === 0
                          ? sideBarChats
                          : sideBarChats.filter((c) =>
                              favoriteChatIds.has(c.chat_id),
                            );
                      const filteredChats = normalizedSearch
                        ? chatsToShow.filter((c) =>
                            (c.chat_name || "Untitled chat")
                              .toLowerCase()
                              .includes(normalizedSearch),
                          )
                        : chatsToShow;

                      if (filteredChats.length === 0) {
                        return (
                          <div className="rounded-xl border border-dashed border-[#E5DDD3] px-4 py-8 text-center text-sm text-[#8E8378]">
                            No chats found
                          </div>
                        );
                      }

                      return filteredChats.map((chat) => {
                        const isFavorite = favoriteChatIds.has(chat.chat_id);
                        return (
                          <li
                            key={chat.chat_id}
                            className="relative rounded-xl transition hover:bg-[#F5EFE6] dark:hover:bg-[#2C405F] overflow-visible"
                          >
                            <div className="flex items-center gap-3 px-3 py-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavoriteChat(chat.chat_id);
                                }}
                                className="rounded-full p-1.5 transition hover:bg-[#EFE6DC] dark:hover:bg-[#455D7D]"
                              >
                                <Star
                                  size={16}
                                  fill={isFavorite ? "#6E829E" : "transparent"}
                                  className={
                                    isFavorite
                                      ? "text-[#2C405F] dark:text-[#6E829E]"
                                      : "text-[#8E8378] dark:text-[#9AAABF]"
                                  }
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleHistoryChatClick(chat.chat_id)
                                }
                                className="flex flex-1 flex-col items-start text-left"
                              >
                                <span className="text-sm font-medium text-[#1D1B19] dark:text-[#E3E6EA]">
                                  {chat.chat_name || "Untitled chat"}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={(e) =>
                                  handleHistoryMenuToggle(e, chat.chat_id)
                                }
                                className="rounded-xl bg-transparent p-2 text-[#8E8378] dark:text-[#9AAABF] transition hover:bg-[#EFE6DC] dark:hover:bg-[#455D7D]"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                              {historyMenuState.chatId === chat.chat_id && (
                                <div
                                  className={`absolute right-2 z-10 w-40 rounded-xl border border-[#E5DDD3] dark:border-[#455D7D] bg-white dark:bg-[#2C405F] py-1 shadow-lg ${
                                    historyMenuState.placement === "above"
                                      ? "bottom-12"
                                      : "top-12"
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[#1D1B19] dark:text-[#E3E6EA] transition hover:bg-[#F5EFE6] dark:hover:bg-[#455D7D]"
                                    onClick={() => {
                                      setSelectedChatForRename(chat);
                                      setRenameInputValue(chat.chat_name || "");
                                      setShowRenameDialog(true);
                                      setHistoryMenuState({
                                        chatId: null,
                                        placement: "below",
                                      });
                                    }}
                                  >
                                    Rename chat
                                  </button>
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 transition hover:bg-red-50 dark:hover:bg-red-900/20"
                                    onClick={() =>
                                      handleHistoryActions("remove", chat)
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      });
                    })()}{" "}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* PWA Mobile History Panel - Bottom slide-up (only for PWA) */}
      {isInPWA && sideBar && (
        <aside
          className={`fixed left-0 right-0 bottom-0 z-40 flex min-h-[70vh] max-h-[75vh] flex-col overflow-hidden rounded-t-[32px] border border-[#E5DDD3] border-b-0 bg-[#FDFAF7] shadow-[0_-35px_90px_rgba(31,27,22,0.22)] backdrop-blur-2xl transition-all duration-300 ${
            sideBar
              ? "translate-y-0 opacity-100"
              : "translate-y-[110%] opacity-0 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between border-b border-[#E5DDD3] px-6 py-4">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold text-[#1D1B19]">
                Chat History
                <span className="text-sm font-normal text-[#8E8378]">
                  ({sideBarChats.length})
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSideBar(false)}
              className="rounded-full bg-transparent p-2 text-[#8E8378] transition hover:bg-[#F5EFE6]"
              aria-label="Close history"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="flex border-b border-[#E5DDD3]">
              <button
                type="button"
                onClick={() => setActiveHistoryTab(0)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  activeHistoryTab === 0
                    ? "border-b-2 border-[#2C405F] text-[#2C405F]"
                    : "text-[#8E8378] hover:text-[#2C405F]"
                }`}
              >
                All ({sideBarChats.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveHistoryTab(1)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  activeHistoryTab === 1
                    ? "border-b-2 border-[#2C405F] text-[#2C405F]"
                    : "text-[#8E8378] hover:text-[#2C405F]"
                }`}
              >
                Favorites ({favoriteChatIds.size})
              </button>
            </div>

            <div className="flex h-[calc(100%-60px)] flex-col gap-3 p-4">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8378]"
                  size={16}
                />
                <input
                  type="text"
                  value={
                    activeHistoryTab === 0
                      ? allHistorySearch
                      : favoritesHistorySearch
                  }
                  onChange={(e) =>
                    activeHistoryTab === 0
                      ? setAllHistorySearch(e.target.value)
                      : setFavoritesHistorySearch(e.target.value)
                  }
                  placeholder="Search incident..."
                  className="h-10 w-full rounded-xl border border-[#E5DDD3] dark:border-[#455D7D] bg-white dark:bg-[#2C405F] pl-10 pr-4 text-sm text-[#1D1B19] dark:text-[#E3E6EA] placeholder-[#8E8378] dark:placeholder-[#9AAABF] focus:border-[#2C405F] dark:focus:border-[#6E829E] focus:outline-none focus:ring-2 focus:ring-[#2C405F]/20 dark:focus:ring-[#6E829E]/20"
                />
              </div>

              <div
                ref={historyScrollRef}
                className="flex-1 overflow-y-auto custom-scrollbar overflow-x-visible"
              >
                {loadingChats ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-sm text-[#8E8378] dark:text-[#9AAABF]">
                      Loading chats...
                    </div>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {(() => {
                      const normalizedSearch = (
                        activeHistoryTab === 0
                          ? allHistorySearch
                          : favoritesHistorySearch
                      )
                        .trim()
                        .toLowerCase();
                      const chatsToShow =
                        activeHistoryTab === 0
                          ? sideBarChats
                          : sideBarChats.filter((c) =>
                              favoriteChatIds.has(c.chat_id),
                            );
                      const filteredChats = normalizedSearch
                        ? chatsToShow.filter((c) =>
                            (c.chat_name || "Untitled chat")
                              .toLowerCase()
                              .includes(normalizedSearch),
                          )
                        : chatsToShow;

                      if (filteredChats.length === 0) {
                        return (
                          <div className="rounded-xl border border-dashed border-[#E5DDD3] dark:border-[#455D7D] px-4 py-8 text-center text-sm text-[#8E8378] dark:text-[#9AAABF]">
                            No chats found
                          </div>
                        );
                      }

                      return filteredChats.map((chat) => {
                        const isFavorite = favoriteChatIds.has(chat.chat_id);
                        return (
                          <li
                            key={chat.chat_id}
                            className="relative rounded-xl transition hover:bg-[#F5EFE6] overflow-visible"
                          >
                            <div className="flex items-center gap-3 px-3 py-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavoriteChat(chat.chat_id);
                                }}
                                className="rounded-full p-1.5 transition hover:bg-[#EFE6DC]"
                              >
                                <Star
                                  size={16}
                                  fill={isFavorite ? "#2C405F" : "transparent"}
                                  className={
                                    isFavorite
                                      ? "text-[#2C405F]"
                                      : "text-[#8E8378]"
                                  }
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleHistoryChatClick(chat.chat_id)
                                }
                                className="flex flex-1 flex-col items-start text-left"
                              >
                                <span className="text-sm font-medium text-[#1D1B19]">
                                  {chat.chat_name || "Untitled chat"}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={(e) =>
                                  handleHistoryMenuToggle(e, chat.chat_id)
                                }
                                className="rounded-xl bg-transparent p-2 text-[#8E8378] transition hover:bg-[#EFE6DC]"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                              {historyMenuState.chatId === chat.chat_id && (
                                <div
                                  className={`absolute right-2 z-10 w-40 rounded-xl border border-[#E5DDD3] bg-white py-1 shadow-lg ${
                                    historyMenuState.placement === "above"
                                      ? "bottom-12"
                                      : "top-12"
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[#1D1B19] transition hover:bg-[#F5EFE6]"
                                    onClick={() => {
                                      setSelectedChatForRename(chat);
                                      setRenameInputValue(chat.chat_name || "");
                                      setShowRenameDialog(true);
                                      setHistoryMenuState({
                                        chatId: null,
                                        placement: "below",
                                      });
                                    }}
                                  >
                                    Rename chat
                                  </button>
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 transition hover:bg-red-50"
                                    onClick={() =>
                                      handleHistoryActions("remove", chat)
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      });
                    })()}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Rename Dialog */}
      {showRenameDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-80 max-w-md rounded-2xl border border-[#E5DDD3] dark:border-[#455D7D] bg-white dark:bg-[#2C405F] p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-[#1D1B19] dark:text-[#E3E6EA]">
              Rename chat
            </h2>
            <input
              type="text"
              value={renameInputValue}
              onChange={(e) => setRenameInputValue(e.target.value)}
              placeholder="Enter chat title"
              className="mb-4 w-full rounded-lg border border-[#E5DDD3] dark:border-[#455D7D] bg-white dark:bg-[#122135] px-3 py-2 text-sm text-[#1D1B19] dark:text-[#E3E6EA] placeholder-[#8E8378] dark:placeholder-[#9AAABF] focus:border-[#2C405F] dark:focus:border-[#6E829E] focus:outline-none focus:ring-2 focus:ring-[#2C405F]/20 dark:focus:ring-[#6E829E]/20"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRenameDialog(false);
                  setSelectedChatForRename(null);
                  setRenameInputValue("");
                }}
                className="rounded-lg bg-[#F5EFE6] dark:bg-[#455D7D] px-4 py-2 text-sm text-[#1D1B19] dark:text-[#E3E6EA] transition hover:bg-[#EFE6DC] dark:hover:bg-[#6E829E]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedChatForRename) {
                    handleHistoryActions(
                      "rename",
                      selectedChatForRename,
                      renameInputValue,
                    );
                  }
                  setShowRenameDialog(false);
                  setSelectedChatForRename(null);
                  setRenameInputValue("");
                }}
                className="rounded-lg bg-[#2C405F] dark:bg-[#6E829E] px-4 py-2 text-sm text-white dark:text-[#0A1423] transition hover:bg-[#1e2e45] dark:hover:bg-[#9AAABF]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token Limit Reached Modal */}
      {showTokenLimitModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowTokenLimitModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1A2D47] rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 border border-[#E5DDD3] dark:border-[#2C405F]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FEF3C7] dark:bg-[#78350F]/30">
                <AlertTriangle className="w-5 h-5 text-[#D97706]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1D1B19] dark:text-white">
                Token Limit Reached
              </h3>
            </div>

            <p className="text-sm text-[#6B6B6B] dark:text-[#8B9DB5] mb-6">
              {isFreeFromGate
                ? "You've used all your available tokens for this billing period. Upgrade to Pro for higher limits and continued access."
                : "You've used all your available tokens for this billing period. You can add more tokens or wait for your quota to reset at the start of the next cycle."}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowTokenLimitModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-[#E5DDD3] dark:border-[#2C405F] text-[#1D1B19] dark:text-[#E3E6EA] text-sm font-medium hover:bg-[#F3ECE4] dark:hover:bg-[#2C405F] transition-colors cursor-pointer"
              >
                Close
              </button>
              {isFreeFromGate && (
                <button
                  onClick={() => {
                    setShowTokenLimitModal(false);
                    window.location.href = "/pricing";
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[#E8922A] hover:bg-[#D4831F] text-white text-sm font-semibold transition-colors cursor-pointer"
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Container */}
      <NotificationContainer
        notifications={notifications}
        onClose={removeNotification}
      />
    </div>
  );
}
